import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, ToggleLeft, ToggleRight, Copy, Check } from 'lucide-react';
import { getMembers, createMember, updateMember, toggleMember, deleteMember } from '../api/members';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import FilterBar from '../components/FilterBar';
import usePageTitle from '../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';

const ROLE_STYLE = {
  EDITOR:            'bg-blue-50 text-blue-700',
  EDITOR_ORDER_ONLY: 'bg-purple-50 text-purple-700',
  VIEWER:            'bg-gray-100 text-gray-600',
};

const EMPTY_FORM = { username: '', password: '', displayName: '', email: '', role: 'EDITOR' };

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="ml-1 p-0.5 text-gray-400 hover:text-blue-600 transition-colors" title="Copy login username">
      {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
    </button>
  );
}

export default function Members() {
  usePageTitle('Team');
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['members', page],
    queryFn: () => getMembers(page).then((r) => r.data.data),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['members'] });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      modal?.mode === 'edit' ? updateMember(modal.data.id, payload) : createMember(payload),
    onSuccess: () => { invalidate(); closeModal(); },
    onError: (err) => setFormError(err.response?.data?.message || 'Something went wrong.'),
  });

  const toggleMutation = useMutation({
    mutationFn: (id) => toggleMember(id),
    onSuccess: () => invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteMember(id),
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
  });

  const openCreate = () => { setForm(EMPTY_FORM); setFormError(''); setModal({ mode: 'create' }); };

  const openEdit = (member) => {
    setForm({ username: member.username, password: '', displayName: member.displayName || '', email: member.email || '', role: member.role });
    setFormError('');
    setModal({ mode: 'edit', data: member });
  };

  const closeModal = () => setModal(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (modal?.mode === 'create') {
      saveMutation.mutate({ username: form.username, password: form.password, displayName: form.displayName || null, email: form.email || null, role: form.role });
    } else {
      saveMutation.mutate({ displayName: form.displayName || null, email: form.email || null, role: form.role, password: form.password || null });
    }
  };

  const members = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t('members.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{data?.totalElements ?? 0} {t('members.memberCount')}</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          <Plus size={15} /> {t('members.add')}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : members.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('members.noMembers')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500">{t('members.memberCount')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('members.loginUsername')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('members.role')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.status')}</th>
                <th className="px-4 py-3 w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map((m) => {
                const shortName = m.username.startsWith(user?.vendorId + '_')
                  ? m.username.slice(user.vendorId.length + 1)
                  : m.username;
                return (
                  <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{m.displayName || shortName}</p>
                      {m.email && <p className="text-xs text-gray-400 mt-0.5">{m.email}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-lg">
                        {m.username}
                      </span>
                      <CopyButton text={m.username} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${ROLE_STYLE[m.role]}`}>
                        {t(`roles.${m.role}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {m.active
                        ? <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">{t('common.active')}</span>
                        : <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{t('common.inactive')}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => toggleMutation.mutate(m.id)} title={m.active ? 'Deactivate' : 'Activate'}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                          {m.active ? <ToggleRight size={16} className="text-blue-500" /> : <ToggleLeft size={16} />}
                        </button>
                        <button onClick={() => openEdit(m)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => setDeleteTarget(m)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {modal && (
        <Modal title={modal.mode === 'create' ? t('members.addMember') : t('members.editMember')} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {modal.mode === 'create' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-500">
                    {user?.vendorId}_
                  </span>
                  <input type="text" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                    required autoFocus
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john" />
                </div>
                <p className="text-xs text-gray-400 mt-1">Login username will be: <span className="font-mono">{user?.vendorId}_{form.username || 'john'}</span></p>
              </div>
            )}

            {modal.mode === 'edit' && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                <p className="text-xs text-gray-400">Login username (cannot change)</p>
                <p className="text-sm font-mono font-medium text-gray-700">{modal.data.username}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password {modal.mode === 'create' && <span className="text-red-500">*</span>}
                {modal.mode === 'edit' && <span className="text-xs text-gray-400 font-normal ml-1">(leave blank to keep current)</span>}
              </label>
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={modal.mode === 'create'} minLength={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('members.passwordHint')} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('members.displayName')}</label>
                <input type="text" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('common.optional')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('common.optional')} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('members.role')} <span className="text-red-500">*</span></label>
              <div className="space-y-2">
                {[
                  { value: 'EDITOR',            labelKey: 'roles.EDITOR',            descKey: 'members.editorDesc' },
                  { value: 'EDITOR_ORDER_ONLY', labelKey: 'roles.EDITOR_ORDER_ONLY', descKey: 'members.orderManagerDesc' },
                  { value: 'VIEWER',            labelKey: 'roles.VIEWER',            descKey: 'members.viewerDesc' },
                ].map((r) => (
                  <label key={r.value} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    form.role === r.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input type="radio" name="role" value={r.value} checked={form.role === r.value}
                      onChange={() => setForm({ ...form, role: r.value })} className="mt-0.5 accent-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{t(r.labelKey)}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{t(r.descKey)}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {formError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
              <button type="submit" disabled={saveMutation.isPending} className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {saveMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {deleteTarget && (
        <Modal title={t('members.removeMember')} onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-600"><span className="font-medium text-gray-900">{deleteTarget.displayName || deleteTarget.username}</span>? {t('common.cannotBeUndone')}</p>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
            <button onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors">
              {deleteMutation.isPending ? t('common.deleting') : t('common.remove')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
